import { BUILD } from "../env.ts";

/**
 * Read-only probes for end-to-end tests (dev and preview builds only):
 * where things are on screen, so Playwright can tap and drag through the
 * real Phaser input path. Never shipped in production builds.
 */
export interface TestHooks {
  /** Opens a level directly, skipping the walk to the house. */
  app?: { startLevel(houseId: "sorting" | "colors" | "tiles" | "logic" | "fill" | "arcade", levelId: number): void };
  fill?: {
    /** Centre of a box slot (row-major index). */
    slotPoint(cell: number): { x: number; y: number } | null;
    /** Centre of the shelf stack of this roll kind ("R", "D3", "H"). */
    rollPoint(roll: string): { x: number; y: number } | null;
    /** The fewest rolls from here. */
    solution(): { roll: string; cell: number }[];
    busy(): boolean;
    won(): boolean;
  };
  arcade?: {
    /** The level on screen (hooks outlive a scene, so tests wait for the new one). */
    levelId(): number;
    /** A point inside a pile's column (a tap there spends a coin on it). */
    columnPoint(column: number): { x: number; y: number } | null;
    /** The fewest coins from here, as columns. */
    solution(): number[];
    /** The claw is still moving. */
    busy(): boolean;
    won(): boolean;
  };
  city?: { houseScreenPoint(id: string): { x: number; y: number } | null; avatarScreenPoint(): { x: number; y: number } };
  sorting?: {
    itemPoint(itemId: string): { x: number; y: number } | null;
    binPoint(binId: string): { x: number; y: number } | null;
    solution(): { itemId: string; toBinId: string }[];
    won(): boolean;
  };
  colors?: {
    vialPoint(vialId: string): { x: number; y: number } | null;
    solution(): { from: string; to: string }[];
    won(): boolean;
  };
  tiles?: {
    cellPoint(cellId: string): { x: number; y: number } | null;
    /** Cells to step on, in order, for the tidiest route from here. */
    solution(): string[];
    bag(): string[];
    won(): boolean;
  };
  logic?: {
    /** A point inside a column of the tower (a tap there drops the marble in hand). */
    columnPoint(column: number): { x: number; y: number } | null;
    railPoint(hopper: number): { x: number; y: number } | null;
    /** The fewest drops from here. */
    solution(): { hopper: number; column: number }[];
    /** A marble is still falling. */
    busy(): boolean;
    won(): boolean;
  };
}

export function registerTestHooks(patch: Partial<TestHooks>): void {
  if (BUILD.isProduction || typeof window === "undefined") return;
  const target = window as unknown as { __nanoTest?: TestHooks };
  target.__nanoTest = { ...(target.__nanoTest ?? {}), ...patch };
}
